// On-device storage for a photo of the tick.
//
// The bite log has always had a photo input — the only file input on the site — and it
// silently threw the photo away: the preview URL was revoked on submit and the entry
// was saved without it. An input that discards what you gave it is worse than no input,
// so this keeps the photo instead.
//
// PRIVACY: IndexedDB on the user's own device, exactly like the log itself. There is no
// upload, no fetch, no object URL that outlives the page. The photo is downscaled before
// storage so a modern phone camera cannot fill the origin's quota with one bite, and
// deleting an entry deletes its photo with it.

const DB_NAME = "lsm_photos";
const DB_VERSION = 1;
const STORE = "photos";

/** Long edge, in pixels. Enough to show a tick's markings, small enough to keep. */
export const MAX_EDGE = 1280;
export const JPEG_QUALITY = 0.82;

function supported(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
  });
}

function tx<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
        t.oncomplete = () => db.close();
      }),
  );
}

/**
 * Shrink an image so the long edge is at most MAX_EDGE. Returns the original blob
 * untouched if the browser cannot do the work — a big photo beats no photo.
 */
export async function downscale(file: Blob): Promise<Blob> {
  if (typeof document === "undefined" || typeof createImageBitmap === "undefined") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1) {
      bitmap.close();
      return file;
    }
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    return blob ?? file;
  } catch {
    return file;
  }
}

export async function putPhoto(file: Blob): Promise<string | undefined> {
  if (!supported()) return undefined;
  try {
    const blob = await downscale(file);
    const id = `photo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    await tx("readwrite", (store) => store.put(blob, id));
    return id;
  } catch {
    return undefined;
  }
}

export async function getPhoto(id: string): Promise<Blob | undefined> {
  if (!supported()) return undefined;
  try {
    const blob = await tx<Blob | undefined>("readonly", (store) => store.get(id));
    return blob instanceof Blob ? blob : undefined;
  } catch {
    return undefined;
  }
}

export async function deletePhoto(id: string): Promise<void> {
  if (!supported()) return;
  try {
    await tx("readwrite", (store) => store.delete(id));
  } catch {
    // Nothing to do — the entry is going away regardless.
  }
}
